import { useMultipleUserProfiles } from "@jugl-web/domain-resources/users/hooks/useMultipleUserProfiles";
import { UpdateTicketPayload } from "@jugl-web/rest-api";
import { TicketListItemModel } from "@jugl-web/rest-api/tickets/models/TicketListItem";
import { AvatarStack } from "@jugl-web/ui-components";
import { TableCell } from "@jugl-web/ui-kit";
import { TicketAssigneesPickerPopover } from "@web-src/modules/client-support/components/TicketAssigneesPickerPopover";
import { FC, useCallback, useEffect, useMemo, useState } from "react";
import { AppearingPlaceholderText } from "../AppearingPlaceholderText";
import { ReactComponent as UserBlankIcon } from "./assets/user-blank.svg";

interface AssigneesCellContentProps {
  entityId: string;
  ticket: TicketListItemModel;
  onUpdate: (data: UpdateTicketPayload) => void;
}

export const AssigneesCellContent: FC<AssigneesCellContentProps> = ({
  entityId,
  ticket,
  onUpdate,
}) => {
  const ticketAssigneeIds = useMemo(
    () => ticket.assignees.map((a) => a.user_id),
    [ticket.assignees]
  );

  const [internalAssigneeIds, setInternalAssigneeIds] =
    useState<string[]>(ticketAssigneeIds);

  const internalAssigneeIdsSorted = useMemo(
    () => [...internalAssigneeIds].sort(),
    [internalAssigneeIds]
  );

  const { profiles } = useMultipleUserProfiles({
    entityId,
    userIds: internalAssigneeIdsSorted,
  });

  const handleSelectionConfirm = useCallback(
    (selectedAssigneeIds: string[]) => {
      onUpdate({ assignees: selectedAssigneeIds });
    },
    [onUpdate]
  );

  // Sync internal assignees with ticket assignees whenever it changes
  useEffect(() => {
    setInternalAssigneeIds(ticketAssigneeIds);
  }, [ticketAssigneeIds]);

  return (
    <TicketAssigneesPickerPopover
      assigneeIds={ticketAssigneeIds}
      onSelectionChange={setInternalAssigneeIds}
      onSelectionConfirm={handleSelectionConfirm}
      renderTrigger={({ Trigger, triggerRef, isOpen }) => (
        <Trigger
          as={TableCell}
          ref={triggerRef}
          isFocused={isOpen}
          isHoverable
          classNames={{ content: "min-w-0" }}
        >
          {profiles.length > 0 ? (
            <AvatarStack
              className="flex max-w-full min-w-0"
              size="sm"
              maxCount={6}
              members={profiles.map((profile) => ({
                firstName: profile.firstName || "",
                lastName: profile.lastName || "",
                imageUrl: profile.avatar || null,
              }))}
              shouldShowNameForSingleMember
              singleMemberLabelClassName="ml-2 min-w-0 truncate text-[13px] leading-[1.4] font-normal text-dark"
            />
          ) : (
            <AppearingPlaceholderText
              icon={<UserBlankIcon />}
              label="Select assignees"
              isStatic={isOpen}
            />
          )}
        </Trigger>
      )}
    />
  );
};
